import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useTranslation } from 'react-i18next';
import { seoUrlTournament } from '~spa/Utils/SeoUrls';
import {
    setSiteScrollDisabledDesktop,
    setSiteScrollDisabled,
} from '~spa/Utils/WrapperIntegration/Ucf.js';
import { getIsMobileWrapperFlag } from '~shared/utils/user';
import { useWidgetDimensions } from './utils';
import MoreGamesPopup from './MoreGamesPopup';
import { fetchEvents } from './services';

import './styles.scss';

import {
    I18N_CHANGE_EVENT,
    MORE_GAMES_GTM_OPEN,
    MORE_GAMES_GTM_CLOSE,
} from './constants';

const calculateMaxHeight = (widgetRef, widgetHeaderRef) => {
    /*
        The maximum height over the overlay
        (including the header and footer) should be either
        a) viewport height -100px, or
        b) 400px
    */
    if (widgetRef.current && widgetHeaderRef.current) {
        const domRect = widgetRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const height_option_1 = (windowHeight - 100) - domRect.top;
        const height_option_2 = 400;
        const height = height_option_1 < height_option_2 ? height_option_1 : height_option_2;

        const {clientHeight: headerHeight} = widgetHeaderRef.current;

        return height - headerHeight;
    }

    return 0;
};

const calculateMaxLeft = (widgetRef) => {
    if (widgetRef.current) {
        const domRect = widgetRef.current.getBoundingClientRect();

        return domRect.left;
    }

    return 0;
};

const calculateTop = (widgetRef) => {
    if (widgetRef.current) {
        const domRect = widgetRef.current.getBoundingClientRect();

        return domRect.bottom;
    }

    return 0;
};

const calculateWidth = (widgetRef) => {
    if (widgetRef.current) {
        return widgetRef.current.clientWidth;
    }

    return 0;
};

const calculateBgHeight = (widgetRef) => {
    if (widgetRef.current) {
        const bodyHeight = document.querySelector('body').scrollHeight;
        const bodyTop = document.querySelector('body').getBoundingClientRect().top;
        const widgetRect = widgetRef.current.getBoundingClientRect();

        return (bodyHeight + bodyTop) - (widgetRect.bottom);
    }

    return 0;
};

const filterCurrentEvent = (eventId, eventList) => {
    return eventList.filter(event => Number(event.id) !== Number(eventId));
};


const sortEvents = (events, orderList) => {
    const sorted = events.sort((eventA, eventB) => {
        const idA = eventA.id;
        const idB = eventB.id;

        let posA, posB;
        for (let i = 0; i < orderList.length; i++) {
            if (idA === orderList[i]['event_id']) {
                posA = i;
            } else if (idB === orderList[i]['event_id']) {
                posB = i;
            }
            if (posA !== undefined && posB !== undefined) {
                break;
            }
        }

        return posA - posB;
    });

    return sorted;
};

const MoreGamesEvents = ({
    tournamentSlug,
    categorySlug,
    sportSlug,
    tournamentId,
    eventId,
    categoryId,
    sportId,
    gtmAttribute,
}) => {

    const widgetReference = React.useRef();
    const widgetHeaderReference = React.useRef();

    const body = document.querySelector('body');

    const {
        elemHeight,
        elemWidth,
    } = useWidgetDimensions(widgetReference);

    const {
        elemHeight: _bgHeight,
        elemWidth: _bgWidth,
    } = useWidgetDimensions(body);

    const [ t ] = useTranslation();
    const [ isPopupOpen, setPopupOpen ] = useState(false);
    const [ maxHeight, setMaxHeight ] = useState(0);
    const [ left, setLeft ] = useState(0);
    const [ top, setTop ] = useState(0);
    const [ width, setWidth ] = useState(0);
    const [ bgHeight, setBgHeight ] = useState(0);
    const [ events, setGameEvents ] = useState( [] );
    const [ gameEventsOrder, setGameEventsOrder ] = useState( [] );
    const [ tournamentUrl, setTournamentUrl ] = useState('');
    const isMobile = getIsMobileWrapperFlag();

    const openPopup = () => {
        calculateSizing();
        setPopupOpen(true);
    };

    const togglePopup = () => {
        if (isPopupOpen) {
            setPopupOpen(false);
        } else {
            setEvents();
            openPopup();
        }
    };

    const calculateSizing = () => {
        setMaxHeight(calculateMaxHeight(widgetReference, widgetHeaderReference));
        setWidth(calculateWidth(widgetReference));
        setLeft(calculateMaxLeft(widgetReference));
        setTop(calculateTop(widgetReference));
        setBgHeight(calculateBgHeight(widgetReference));
    };

    useEffect(() => {
        calculateSizing();
    }, [elemHeight, elemWidth, _bgHeight, _bgWidth]);

    const handleScroll = () => {
        if (isPopupOpen) {
            setLeft(calculateMaxLeft(widgetReference));
            setTop(calculateTop(widgetReference));
        }
    };

    const registerScrollEvent = () => {
        body.addEventListener('scroll', handleScroll);
        document.addEventListener('scroll', handleScroll);
    };

    const unregisterScrollEvent = () => {
        body.removeEventListener('scroll', handleScroll);
        document.removeEventListener('scroll', handleScroll);
    };

    useEffect(() => {
        if (isPopupOpen) {
            registerScrollEvent();
            if (isMobile) {
                setSiteScrollDisabled(true);
            } else {
                setSiteScrollDisabledDesktop(true);
            }

            return unregisterScrollEvent;
        }

        if (isMobile) {
            setSiteScrollDisabled(false);
        } else {
            setSiteScrollDisabledDesktop(false);
        }

        return Function.prototype;
    }, [isPopupOpen]);

    const setEvents = async () => {
        const events = await fetchEvents(
            tournamentSlug,
            categorySlug,
            sportSlug,
            tournamentId,
            eventId,
            categoryId,
            sportId
        );

        if (events) {
            setGameEvents(Object.values(events['events']));
            setGameEventsOrder(events['event_order']);
            const {
                translations:
                {
                    sport: sport_slug = sportSlug,
                    category: category_slug = categorySlug,
                    tournament: tournament_slug = tournamentSlug,
                } = {},
            } = events || {};
            setTournamentUrl(seoUrlTournament(`/${sport_slug}/${category_slug}/${tournament_slug}`, tournamentId));
        }
    };

    useEffect(() => {
        setPopupOpen(false);
        setEvents();
    }, [eventId]);

    const filteredEvents = filterCurrentEvent(eventId, events);
    const sortedEvents = sortEvents(filteredEvents, gameEventsOrder);

    return (
        sortedEvents.length > 0
            ? <div
                className="more-game-events-widget"
                ref={widgetReference}
                onClick={togglePopup}
                data-spectate-product={`${gtmAttribute};${isPopupOpen ? MORE_GAMES_GTM_CLOSE : MORE_GAMES_GTM_OPEN}`}
            >
                <div className="more-game-events-widget__container">
                    <div
                        ref={widgetHeaderReference}
                        className={className('more-game-events-widget__header', {'more-game-events-widget__header--opened': isPopupOpen})}
                    >
                        <span className="more-game-events-widget__change-event" dir="auto">
                            {t(I18N_CHANGE_EVENT)}
                            <span className="more-game-events-widget__counter"> ({sortedEvents.length})</span>
                        </span>
                    </div>
                </div>
                <MoreGamesPopup
                    isOpen = {isPopupOpen}
                    setOpen = {setPopupOpen}
                    maxHeight = {maxHeight}
                    width={width}
                    left={left}
                    top={top}
                    tournamentUrl={tournamentUrl}
                    bgHeight={bgHeight}
                    events={sortedEvents}
                    gtmAttribute={gtmAttribute}
                />
            </div>
        : null
    );
};

MoreGamesEvents.propTypes = {
    tournamentSlug: PropTypes.string.isRequired,
    categorySlug: PropTypes.string.isRequired,
    sportSlug: PropTypes.string.isRequired,
    tournamentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    categoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    sportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    gtmAttribute: PropTypes.string,
};

MoreGamesEvents.defaultProps = {
    gtmAttribute: '',
};

export default MoreGamesEvents;
